import { useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import { CoverageTooltipType } from 'data/types';
import { useCallback, useEffect, useState } from 'react';
import axios from 'utils/api';
import { errorModal, successModal } from 'utils/ui';

// Admin screen for the customer quote view's Heating/Cooling Coverage info
// tooltips (AHA-6796). The codes and composition rules (which sentence
// applies, concatenation order) live in alpine-client code, so this is
// edit-only: no create/delete.
export default function CoverageTooltipsPage() {
  const { user } = useAppSelector((state) => state.auth);
  const [tooltips, setTooltips] = useState<CoverageTooltipType[]>([]);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [draftContent, setDraftContent] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchTooltips = useCallback(async () => {
    try {
      const res = await axios.get('coverage-tooltips');
      setTooltips(res.data as CoverageTooltipType[]);
    } catch {
      errorModal('Something went wrong.');
    }
  }, []);

  useEffect(() => {
    fetchTooltips();
  }, [fetchTooltips]);

  const startEdit = (tooltip: CoverageTooltipType) => {
    setEditingCode(tooltip.code);
    setDraftContent(tooltip.content);
  };

  const cancelEdit = () => {
    setEditingCode(null);
    setDraftContent('');
  };

  const handleSave = async () => {
    if (editingCode === null) return;
    const label =
      tooltips.find((t) => t.code === editingCode)?.label ?? editingCode;
    setSaving(true);
    try {
      await axios.patch('coverage-tooltip', {
        code: editingCode,
        content: draftContent,
        user: user?.name ?? 'Admin'
      });
      cancelEdit();
      await fetchTooltips();
      successModal(`${label} tooltip updated.`);
    } catch {
      errorModal('Something went wrong.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
      <div className='text-3xl font-bold text-gray-900'>Coverage Tooltips</div>
      <div className='text-sm text-gray-600'>
        The sentence templates behind the Heating/Cooling Coverage info tooltips
        on the customer quote view. Which sentence applies (and the kept-furnace
        concatenation) is decided in code; edit wording only.
      </div>
      <table className='w-full table-fixed border-collapse rounded-md bg-white text-left text-sm shadow-sm'>
        <thead>
          <tr className='border-b border-gray-200 text-gray-900'>
            <th className='w-48 px-4 py-3'>Code</th>
            <th className='w-56 px-4 py-3'>Label</th>
            <th className='px-4 py-3'>Content</th>
            <th className='w-40 px-4 py-3' aria-label='Actions' />
          </tr>
        </thead>
        <tbody>
          {tooltips.map((tooltip) => {
            const isEditing = editingCode === tooltip.code;
            return (
              <tr
                key={tooltip.code}
                className='border-b border-gray-100 align-top last:border-b-0'
              >
                <td className='px-4 py-3 font-mono text-xs text-gray-600'>
                  {tooltip.code}
                </td>
                <td className='px-4 py-3 text-gray-900'>{tooltip.label}</td>
                <td className='whitespace-pre-line px-4 py-3 text-gray-700'>
                  {isEditing ? (
                    <textarea
                      aria-label={`${tooltip.label} tooltip content`}
                      value={draftContent}
                      onChange={(e) => setDraftContent(e.target.value)}
                      rows={4}
                      className='w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm'
                    />
                  ) : (
                    tooltip.content
                  )}
                </td>
                <td className='px-4 py-3'>
                  {isEditing ? (
                    <div className='flex gap-2'>
                      <Button
                        text='Save'
                        color='green'
                        onClick={handleSave}
                        disabled={saving || draftContent.trim() === ''}
                      />
                      <Button
                        text='Cancel'
                        color='gray'
                        onClick={cancelEdit}
                        disabled={saving}
                      />
                    </div>
                  ) : (
                    <Button
                      text='Edit'
                      color='secondary'
                      onClick={() => startEdit(tooltip)}
                      disabled={editingCode !== null}
                    />
                  )}
                </td>
              </tr>
            );
          })}
          {tooltips.length === 0 && (
            <tr>
              <td colSpan={4} className='px-4 py-6 text-center text-gray-500'>
                No coverage tooltips found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
